/**
 * Chatbot Service — Groq AI Integration
 *
 * Calls Groq's free API (Llama 3.1) with a proper multi-turn
 * conversation history so the AI always has full context.
 *
 * Setup: add REACT_APP_GROQ_API_KEY to your fixit/.env
 * Get a free key at: https://console.groq.com
 */

const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;
const GROQ_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODEL = 'llama-3.1-8b-instant';
const AI_TIMEOUT_MS = 12000;

const SYSTEM_PROMPT = `You are a knowledgeable home services assistant for SkillHive, a platform that connects customers with skilled local technicians.

You handle TWO types of queries:

1. TROUBLESHOOTING / REPAIR — User has a problem (leak, fault, damage, broken item, etc.)
   - Suggest exactly ONE clear, actionable DIY fix per response
   - Each new response must be a different approach from anything already discussed
   - If dangerous (gas leak, major electrical fault, structural damage), immediately advise calling a professional

2. ADVICE / SUGGESTIONS — User wants recommendations (color, material, product, design, technique, etc.)
   - Give ONE focused, practical recommendation with a brief reason
   - Examples: paint color combos, wood type for furniture, cleaning product for a stain, AC filter type, etc.

Rules for BOTH types:
- Keep it concise: 2 to 4 sentences max
- Use plain, simple language — no technical jargon unless necessary
- Do NOT ask follow-up questions — just give the answer
- Do NOT use markdown, bullet points, headers, or any formatting — plain paragraph only
- If the user's message contains pronouns like "it", "that", "this" — they refer to what was discussed in the previous message
- For Cleaning: this includes house windows, glass panels, glass towers, mirrors, tiles, fabrics, surfaces, and any stain/dirt removal

IMPORTANT — Category detection:
- The user may have initially selected a category but then described a problem that belongs to a DIFFERENT category.
- Based on the user's ACTUAL problem description, determine the correct service category from this list: Electrical, Plumbing, Carpentry, Painting, AC / Appliance, Cleaning, Other.
- At the very end of EVERY response, append a tag on a new line in this exact format: [CATEGORY: <detected category>]
- Example: if user selected Electrical but describes a water leak, your response should end with [CATEGORY: Plumbing]
- Always answer the user's actual problem regardless of the initially selected category.`;

/**
 * Get a response from Groq using full conversation history.
 *
 * @param {object} params
 * @param {string}   params.category           - e.g. "Electrical", "Plumbing"
 * @param {number}   params.attemptNumber      - 1, 2, or 3
 * @param {Array}    params.conversationHistory - [{role:'user'|'assistant', content:string}]
 * @returns {Promise<string>}
 */
export async function getSolution({ category, attemptNumber, conversationHistory }) {
    const latestUserMessage = [...(conversationHistory || [])]
        .reverse()
        .find((m) => m?.role === 'user')?.content || '';

    if (!GROQ_API_KEY) {
        return buildFallbackSolution(category, attemptNumber, latestUserMessage);
    }

    const systemMessage =
        SYSTEM_PROMPT +
        `\n\nService category for this conversation: ${category}.` +
        (attemptNumber > 1
            ? `\nThis is attempt ${attemptNumber} of 3 — you have already given ${attemptNumber - 1} suggestion(s) in this conversation. Provide a clearly different approach.`
            : '');

    const response = await Promise.race([
        fetch(GROQ_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${GROQ_API_KEY}`
            },
            body: JSON.stringify({
                model: GROQ_MODEL,
                messages: [
                    { role: 'system', content: systemMessage },
                    ...conversationHistory
                ],
                temperature: 0.7,
                max_tokens: 220
            })
        }),
        new Promise((_, reject) => {
            setTimeout(() => reject(new Error('AI request timed out')), AI_TIMEOUT_MS);
        })
    ]);

    if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error?.message || `Groq API error: ${response.status}`);
    }

    const data = await response.json();
    const rawContent = data.choices?.[0]?.message?.content?.trim();

    if (!rawContent) {
        throw new Error('Empty response from AI. Please try again.');
    }

    // Parse out the [CATEGORY: xxx] tag if present
    const categoryMatch = rawContent.match(/\[CATEGORY:\s*(.+?)\]\s*$/);
    const detectedCategory = categoryMatch ? categoryMatch[1].trim() : null;
    const solution = rawContent.replace(/\[CATEGORY:\s*.+?\]\s*$/, '').trim();

    return { solution, detectedCategory };
}

function buildFallbackSolution(category, attemptNumber, latestUserMessage) {
    const lower = String(latestUserMessage || '').toLowerCase();
    const cat = String(category || 'Other');

    const dangerDetected = /spark|burning|smell of gas|gas leak|shock|smoke|short circuit|fire/i.test(lower);
    if (dangerDetected) {
        return {
            solution: 'This may be unsafe. Switch off power or gas from the main source, keep distance, and do not try DIY right now. Please book a professional immediately for a safe inspection.',
            detectedCategory: /gas|leak/.test(lower) ? 'Plumbing' : 'Electrical',
        };
    }

    const tipsByCategory = {
        'Electrical': [
            'Turn off the main switch, then check whether the MCB for that room has tripped. Reset it once and test with one appliance at a time to identify the faulty load.',
            'Switch off the affected circuit and tighten loose switch-board terminals only if you are trained; otherwise avoid touching wiring and isolate that switch until a technician checks it.',
            'Try replacing the device first (bulb/adapter), then test the same point with a different working device to confirm whether the fault is in the appliance or wall point.'
        ],
        'Plumbing': [
            'Close the nearest valve and dry the area fully, then run water slowly to locate the exact leak point before applying any temporary tape or seal.',
            'If drainage is slow, pour hot water in stages and clear the trap first; avoid mixing strong chemicals because it can damage pipes and joints.',
            'For low pressure, clean faucet aerators and check if only one fixture is affected; if all fixtures are weak, the issue is likely in the main line.'
        ],
        'Carpentry': [
            'Tighten all visible screws first, then add a small wood glue layer in loose joints and clamp until set to stop wobble.',
            'If a door is scraping, check hinge screws and tighten or replace with longer screws before trimming wood.',
            'For minor surface damage, sand lightly along grain, apply matching filler, then finish with polish or paint after drying.'
        ],
        'Painting': [
            'Clean the wall and let it dry completely, then apply primer on patches before repainting to avoid uneven tone.',
            'If paint is peeling, scrape loose paint, sand edges smooth, and repaint in thin coats instead of one thick layer.',
            'For damp marks, fix the moisture source first; repainting without solving dampness will make stains reappear quickly.'
        ],
        'AC / Appliance': [
            'Switch off power for 5 minutes and restart once, then clean filters and ensure vents are not blocked for better airflow.',
            'If cooling is weak, set mode to cool, lower setpoint, and check whether outdoor unit is running before assuming gas issue.',
            'For appliance trips, plug into a separate socket and test without extension boards to rule out local socket overload.'
        ],
        'Cleaning': [
            'Start with a mild cleaner on a hidden spot, wipe with microfiber, and avoid abrasive scrubbers on glass or polished surfaces.',
            'For grease stains, pre-treat with dish soap and warm water, let it sit for a few minutes, then blot rather than rub hard.',
            'For streak-free glass, use a small amount of cleaner and wipe top-to-bottom with a dry microfiber cloth immediately.'
        ],
        'Other': [
            'Start by isolating the exact area where the issue begins, then test one change at a time so the root cause is easier to identify.',
            'Check for loose connections, power supply, and visible damage first since these account for many common failures.',
            'If the issue repeats after one safe attempt, avoid repeated DIY and book a professional to prevent bigger damage.'
        ]
    };

    const bucket = tipsByCategory[cat] || tipsByCategory.Other;
    const idx = Math.max(0, Math.min(bucket.length - 1, (attemptNumber || 1) - 1));
    return {
        solution: bucket[idx],
        detectedCategory: cat,
    };
}

